import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import * as paths from '@dna/paths'
import styles from '@dna/styles'
import { getSectionPosition } from '@dna/utils'
import {
  LitElement,
  createRef,
  css,
  html,
  nothing,
  ref,
  unsafeHTML,
  when,
} from '@lit'

// TODO: refactor to extract fetch logic and have this component focus exclusively on rendering the carousel from an array of items
class DNAProductCarousel extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    trackingKey: {
      type: String,
      attribute: 'tracking-key',
    },
    numberOfProducts: {
      type: Number,
      attribute: 'number-of-products',
    },
    items: {
      type: Array,
    },
    sectionSettings: {
      type: Array,
    },
    customConstructorItemName: {
      type: String,
      attribute: 'custom-constructor-item-name',
    },
  }

  constructor() {
    super()
    this.items = []
    this.displayName = ''
    this.sectionSettings = []
    this.sectionTitleRef = createRef()
    this.carouselSlidePosition = 0
    this.slideChangedOnLoad = false
  }

  get maxItems() {
    return this.numberOfProducts || 20
  }

  get filteredItems() {
    let filtered = this.items ? this.items.filter((item) => item !== null) : []
    const hideOutOfStock = this.sectionSettings.hide_out_of_stock_products
    if (hideOutOfStock) {
      filtered = filtered.filter((item) => item.available)
    }
    return filtered.slice(0, this.maxItems)
  }

  get headline() {
    return this.sectionSettings.headline || this.displayName
  }

  get showNavigation() {
    return this.filteredItems.length > 4 && !this.isMobile
  }

  connectedCallback() {
    super.connectedCallback()

    this.sectionPosition = getSectionPosition(this.closest('.shopify-section'))
    this.addEventListener('sl-slide-change', this._handleSlideChangeOnLoad)
  }

  _handleSlideChangeOnLoad = (event) => {
    // Prevent slide change events from firing Segment events when the carousel is loaded
    if (event.detail.index <= 0) {
      this.slideChangedOnLoad = true
    }
    // Don't keep listening after this is caught for the first time
    this.removeEventListener('sl-slide-change', this._handleSlideChangeOnLoad)
  }

  _trackCarouselScrolled = (event) => {
    // @segment
    Segment.protect(() => {
      if (this.slideChangedOnLoad) {
        this.slideChangedOnLoad = false
      } else {
        const scrollDirection =
          event.detail.index > this.carouselSlidePosition ? 'next' : 'previous'
        this.carouselSlidePosition = event.detail.index

        Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_SCROLLED, {
          product_carousel_section_title:
            this.sectionTitleRef?.value?.textContent.trim(),
          product_carousel_scroll_direction: scrollDirection,
          product_carousel_pod_id: this.dataset.cnstrcRecommendationsPodId,
          section_page_position: this.sectionPosition,
          section_tracking_key: this.trackingKey,
        })
      }
    })
  }

  _trackCarouselCTAClicked = (event) => {
    // @segment
    Segment.protect(() => {
      const $clickedProductCarouselCTA = event.target.closest('sl-button')

      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CTA_CLICKED, {
        product_carousel_section_title:
          this.sectionTitleRef?.value?.textContent.trim(),
        product_carousel_cta_link:
          $clickedProductCarouselCTA.getAttribute('href'),
        product_carousel_cta_text:
          $clickedProductCarouselCTA.textContent.trim(),
        product_carousel_pod_id: this.dataset.cnstrcRecommendationsPodId,
        section_page_position: this.sectionPosition,
        section_tracking_key: this.trackingKey,
      })
    })
  }

  _renderHeader() {
    const headLine = this.sectionSettings?.headline
    const subLine = this.sectionSettings?.subline
    const ctaText = this.sectionSettings?.cta_text
    const ctaUrl = this.sectionSettings?.cta_url
    const showSubline = !!subLine || (!!ctaText && !!ctaUrl)
    const headlineAlignment = this.sectionSettings?.headline_alignment

    return html`
      ${
        headLine
          ? html`<h2 class="product-carousel__header-headline headline__${headlineAlignment}" ${ref(this.sectionTitleRef)}>${unsafeHTML(headLine)}</h2>`
          : nothing
      }
      ${when(
        showSubline,
        () => html`
          <div class="product-carousel__header-subline">
            <div class="product-carousel__header-subtext">${subLine}</div>
            ${
              !this.isMobile
                ? html`<slot name="carousel-button" @click=${this._trackCarouselCTAClicked}></slot>`
                : nothing
            }
          </div>
        `,
        () => nothing,
      )}

    `
  }

  render() {
    if (this.filteredItems.length === 0) return nothing
    const subLine = this.sectionSettings?.subline
    const ctaText = this.sectionSettings?.cta_text
    const ctaUrl = this.sectionSettings?.cta_url
    const noSubline = !subLine && ctaText && ctaUrl

    return html`
      <div>
        <div class="product-carousel__header ${noSubline ? 'no-subline' : ''}">
          ${this._renderHeader()}
        </div>
      </div>
      <div class="product-carousel__products ${
        !this.isMobile && this.filteredItems.length < 4
          ? 'product-carousel__products--center'
          : ''
      }">
        <sl-carousel
          @sl-slide-change=${this._trackCarouselScrolled}
          slides-per-page=${this.isMobile ? 1.5 : 4}
          style="--aspect-ratio: unset; ${
            !this.isMobile ? '--slide-gap: 0px;' : nothing
          }"
          class=${!this.sectionSettings?.cta_url ? 'no-cta' : ''}
          ?navigation=${this.showNavigation}
        >
          ${this.filteredItems.map((product) => {
            return html`
              <sl-carousel-item tabindex="0">
                <dna-product-card
                  .product=${product}
                  tracking-key=${this.trackingKey}
                  section-position=${this.sectionPosition}
                  ?hide-product-color=${this.sectionSettings.hide_product_color}
                  ?hide-badges=${this.sectionSettings.hide_badges}
                  ?hide-wishlist-icon=${this.sectionSettings.hide_wishlist_icon}
                  data-cnstrc-item=${this.customConstructorItemName}
                  data-cnstrc-item-id=${product.id}
                  data-cnstrc-item-name=${product.title}
                  data-cnstrc-item-variation-id=${
                    product?.variants?.[0]?.id ?? nothing
                  }
                  data-cnstrc-strategy-id=${product?.strategy_id ?? nothing}
                  data-cnstrc-recommendations-pod-id=${this.dataset.cnstrcRecommendationsPodId ?? nothing}
                ></dna-product-card>
              </sl-carousel-item>
            `
          })}
          <span
            class="product-carousel__arrow"
            slot="previous-icon"
          >
            ${paths.arrowLeft2}
          </span>
          <span
            class="product-carousel__arrow"
            slot="next-icon"
          >
            ${paths.arrowRight2}
          </span>
        </sl-carousel>
      </div>
      ${when(
        this.isMobile && this.sectionSettings?.cta_url,
        () => html`
          <div class="product-carousel__button">
            <slot name="carousel-button" @click=${this._trackCarouselCTAClicked}></slot>
          </div>
        `,
        () => nothing,
      )}
    `
  }

  static styles = [
    styles.reset,
    styles.container,
    styles.typography,
    css`
      :host {
        position: relative;
      }
      @media screen and (min-width: 1024px) {
        .product-carousel__products {
          display: flex;
        }
      }
      @media screen and (min-width: 1024px) {
        :host sl-carousel {
          width: 100%;
        }
      }
      :host sl-carousel::part(scroll-container) {
        gap: 0;
        --aspect-ratio: 0;
      }
      .product-carousel__products--center sl-carousel::part(scroll-container) {
        justify-content: center;
      }
      :host sl-carousel::part(base) {
        grid-template-areas: 'slides' 'navigation';
        grid-template-columns: 1fr;
        gap: 0;
      }
      :host sl-carousel::part(navigation) {
        margin-left: 160px;
        grid-area: navigation;
        display: flex;
        justify-content: flex-end;
        margin-top: var(--dna-space-6);
        margin-bottom: var(--dna-space-6);
      }
      .product-carousel__arrow {
        color: var(--dna-color-midnight);
      }
      :host sl-carousel::part(navigation-button--previous) {
        margin-right: var(--dna-space-4);
      }
      :host sl-carousel-item {
        height: 100%;
        border-top: 1px solid var(--dna-color-light-grey);
        border-right: 1px solid var(--dna-color-light-grey);
        border-bottom: 1px solid var(--dna-color-light-grey);
      }
      .product-carousel__header {
        margin: var(--dna-space-4);
        margin-top: 0;
      }
      .product-carousel__header-headline {
        margin-top: 0;
        margin-bottom: 0;
        text-align: center;
        font: var(--dna-h3-font);
      }
      .headline__left {
        text-align: left;
      }
      .product-carousel__header-headline p {
        margin: 0;
      }
      .product-carousel__header-subline {
        display: flex;
        gap: var(--dna-space-3);
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      @media screen and (min-width: 1024px) {
        .product-carousel__header-subline {
          gap: unset;
          flex-direction: row;
          justify-content: space-between;
        }
      }
      .product-carousel__header-subtext {
        text-align: center;
        margin-top: var(--dna-space-2);
        font: var(--dna-body-p2-font);
      }
      .product-carousel__button {
        text-align: center;
        margin-top: var(--dna-space-6);
      }
      @media screen and (max-width: 768px) {
        :host sl-carousel::part(scroll-container) {
          padding-left: 0;
        }
        :host sl-carousel.no-cta::part(navigation) {
          position: relative;
          margin-top: 0px;
          margin-bottom: 52px;
        }
        :host sl-carousel.no-cta::part(navigation-button--previous) {
          position: absolute;
          top: 0;
          right: var(--dna-space-20);
        }
        :host sl-carousel.no-cta::part(navigation-button--next) {
          position: absolute;
          top: 0;
          right: var(--dna-space-4);
        }
      }
      @media screen and (min-width: 1024px) {
        :host sl-carousel::part(navigation) {
          margin-right: var(--dna-space-6);
          margin-top: var(--dna-space-6);
          margin-bottom: var(--dna-space-10);
        }
        .product-carousel__header {
          margin: var(--dna-space-6) var(--dna-space-10);
          margin-top: 0;
        }
        .product-carousel__header.no-subline {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .product-carousel__header-headline {
          font: var(--dna-h4-font);
        }
        .product-carousel__header-subtext {
          font: var(--dna-body-p1-font);
          text-align: left;
        }
        .headline__center {
          text-align: center;
        }
        .headline__right {
          text-align: right;
        }
      }
    `,
  ]
}

customElements.define('dna-product-carousel', DNAProductCarousel)
